import React, { FC, useEffect, useState } from "react";
import { Button, Popconfirm, Table, Tag, Typography, message } from "antd";
import type { ColumnGroupType, ColumnType, ColumnsType } from "antd/es/table";
import {
  GetDataOrderListData,
  GetDataOrderListItems,
  GetOrderDetailData,
} from "services/interface";
import { formatDate, formatDateWithTime } from "utils/dateFormat";
import { calculateTimeDifference, getPaymentName, getPaymentStatusName } from "utils/convertData";
import ModalOderDetail from "../OrderTabsCard/ModalOrderDetail";
import { useAppDispatch, useAppSelector } from "../../../store";
import { setLoading } from "store/slices/loadingSlice";
import { useGetOrderDetail, useUpdateOrderComplete } from "hooks/useOrders";
import {
  setDataShippedLimit,
  setDataShippedOffset,
} from "store/slices/orderSlice";
import { PrinterOutlined, SendOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { numberFormat } from "utils/nuberFormat";
import ModalRefundPayment from "../OrderTabsCard/ModalRefundPayment";
import moment from "moment";
import ShippingResultModal from "../OrderPrepareShip/ShippingResultModal";
import { apiGetShippingQueueStatus, checkShippingItems } from "services/ordersApi";
import {
  SHIPPING_LABEL_STATUS_COLUMN_WIDTH,
  ShippingLabelStatusCell,
} from "../ShippingLabelStatusCell";

interface OrderTableListProps {
  data?: GetDataOrderListData;
  IsHiddenPersonInfo?: boolean;
  refetchData: () => void;
  childrenBank: React.ReactNode[];
  refetchTo: () => void;
  hasSpeedMerchant: boolean;
  factory_id: string;
}

const OrderTableList: FC<OrderTableListProps> = ({
  data,
  IsHiddenPersonInfo,
  refetchData,
  childrenBank,
  refetchTo,
  hasSpeedMerchant,
  factory_id
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tabsKey = 2;
  const { mutate: updateOrderComplete } = useUpdateOrderComplete();
  const offset = useAppSelector((state) => state.order.dataShipped.offset);
  const limit = useAppSelector((state) => state.order.dataShipped.limit);
  const [showDetail, setShowDetail] = useState(false);
  const [modalData, setModalData] = useState<GetOrderDetailData>();
  const { mutate } = useGetOrderDetail();
  const [currentPage, setCurrentPage] = useState(1);

  const [showRefund, setShowRefund] = useState(false);

  const [currentTime, setCurrentTime] = useState(moment());
  const [retryOrderId, setRetryOrderId] = useState<string | null>(null);
  const [queueStatus, setQueueStatus] = useState<Record<string, string>>({});
  const [hasSelfByOrderId, setHasSelfByOrderId] = useState<Record<string, boolean>>({});

  const contactData = useAppSelector((state) => state.auth.contact);
  const use_platform_transport = contactData?.use_platform_transport;

  useEffect(() => {
    setCurrentPage(Math.floor(offset / limit) + 1);
  }, [offset, limit]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const fetchQueueStatus = async () => {
      if (!data?.order_data || data.order_data.length === 0) return;
      const orderIds = data.order_data.map((o) => o.order_id);
      try {
        const res = await apiGetShippingQueueStatus(orderIds);
        if (res.status === "success" && res.data) {
          setQueueStatus(res.data);
        }
      } catch (e) {
        console.error("Polling queue status error (shipped tab)", e);
      }
    };

    if (data?.order_data && data.order_data.length > 0) {
      fetchQueueStatus();
      interval = setInterval(fetchQueueStatus, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [data?.order_data]);

  useEffect(() => {
    const fetchSelfShipFlags = async () => {
      if (!data?.order_data || data.order_data.length === 0) {
        setHasSelfByOrderId({});
        return;
      }
      const orderIds = data.order_data.map((o) => o.order_id);
      try {
        const res = await checkShippingItems(orderIds);
        if (res.status === "success" && res.data?.details) {
          const map: Record<string, boolean> = {};
          for (const d of res.data.details) {
            map[d.order_id] = (d.z ?? 0) > 0;
          }
          setHasSelfByOrderId(map);
        }
      } catch (e) {
        console.error("checkShippingItems error (shipped tab)", e);
      }
    };

    if (data?.order_data && data.order_data.length > 0) {
      fetchSelfShipFlags();
    }
  }, [data?.order_data]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, 60000);

    dispatch(setDataShippedOffset(0));
    dispatch(setDataShippedLimit(10));

    return () => clearInterval(interval);
  }, []);

  const columns: ColumnsType<GetDataOrderListItems> = [
    {
      title: "Order No.",
      dataIndex: "order_code",
      key: "order_id",
      render: (_text, record) => {
        let restPrice = -((record.total_order_price + record.transport_price) - (record.discount_amount + record.payment_amount));
        return (
          <Typography.Link
            style={{
              cursor: "pointer",
              fontWeight: "400",
              textDecoration: "underline",
            }}
            type="secondary"
            onClick={async () => {
              dispatch(setLoading(true));

              mutate(
                {
                  key_type: "order_id",
                  group_by: "factory",
                  items: [record.order_id],
                  factory_id: factory_id,
                },
                {
                  onSuccess: async (data) => {

                    setModalData({ ...data.data[0], payment_status: record.payment_status, payment_amount: record.payment_amount, final_price: restPrice });

                    await new Promise((resolve) => setTimeout(resolve, 200));

                    dispatch(setLoading(false));

                    setShowDetail(true);
                  },
                  onError: (error) => {
                    console.log(error);
                    dispatch(setLoading(false));
                  },
                }
              );
            }}
          >
            {_text}
          </Typography.Link>
        );
      },
    },
    {
      // title: 'เวลา',
      // dataIndex: 'order_date',
      render: (_text, record) => {
        return <Typography >
          {_text ? (
            <>
              {calculateTimeDifference(record.order_date, currentTime)}
            </>
          ) : '-'}
        </Typography>
      },
    },
    {
      title: "วันที่สั่งซื้อ",
      dataIndex: "order_date",
      render: (_text) => {
        return (
          <Typography>{_text ? formatDateWithTime(_text) : "-"}</Typography>
        );
      },
    },
    {
      title: "วันที่ต้องส่งสินค้า",
      dataIndex: "due_date",
      render: (_text) => {
        return <Typography>{_text ? formatDate(_text) : "-"}</Typography>;
      },
    },
    {
      title: "ชื่อผู้ซื้อ",
      dataIndex: "ref_name",
      key: "ref_name",
      render: (_text, record) => {
        return (
          <Typography.Link
            style={{
              cursor: "pointer",
              fontWeight: "400",
              textDecoration: "underline",
              maxWidth: "180px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              display: "inline-block",
            }}
            type="secondary"
            onClick={() => {
              if (record.ref_type === "agent") {
                navigate(
                  `/manage-agent/update-agent/${record.ref_id}?section=order&t=${tabsKey}`
                );
              } else if (record.ref_type === "retail") {
                navigate(
                  `/manage-agent/update-retail/${record.ref_id}?section=order&t=${tabsKey}`
                );
              }
            }}
          >
            {_text ?? ''}
            <Typography.Text className="text-order-agent-name">
              {" "}
              {record && record.ref_alias ? `(${record.ref_alias})` : ""}
            </Typography.Text>
          </Typography.Link>
        );
      },
    },
   /* {
      title: "ประเภทลูกค้า",
      dataIndex: "ref_type",
      key: "ref_type",
      render: (_text) => {
        return <Typography>{_text ?? ""}</Typography>;
      },
    },*/
    {
      title: "เบอร์โทรศัพท์",
      dataIndex: "ref_telephone",
      key: "ref_telephone",
      render: (_text) => {
        return <Typography>{_text}</Typography>;
      },
    },
  /*  {
      title: "ประเภทการขนส่ง",
      width: "100px",
      dataIndex: "transport_name",
      render: (_text) => {
        return <Typography>{_text}</Typography>;
      },
    },*/
    {
      title: "ช่องทางการสั่งซื้อ",
      width: "100px",
      dataIndex: "datasource_text",
      key: "datasource_text",
      render: (_text) => {
        return <Typography>{_text}</Typography>;
      },
    },
    {
      title: "ประเภทการชำระเงิน",
      width: "100px",
      dataIndex: "payment_type",
      key: "payment_type",
      render: (_text) => {
        return (
          <Typography style={_text === "COD" ? { color: "#ff4d4f" } : undefined}>
            {getPaymentName(_text, "en")}
          </Typography>
        );
      },
    },
    {
      title: "ราคารวม",
      dataIndex: "result_price",
      render: (_text) => {
        return <Typography>{numberFormat(_text)}</Typography>;
      },
    },
    {
      title: "เครดิตที่ใช้ (COD)",
      dataIndex: "cod_credit_reserved_amount",
      key: "cod_credit_reserved_amount",
      render: (_text, record) => {
        if (record.payment_type !== "COD" || record.cod_credit_reserved_amount == null) {
          return <Typography>-</Typography>;
        }
        const isInsufficient = record.cod_credit_has_debt === true;
        return (
          <Typography style={isInsufficient ? { color: "#ff4d4f" } : undefined}>
            {numberFormat(record.cod_credit_reserved_amount)}
            {isInsufficient && (
              <Tag color="red" style={{ marginLeft: 6 }}>
                เครดิตไม่พอ
              </Tag>
            )}
          </Typography>
        );
      },
    },
    {
      title: "สถานะการชำระเงิน",
      key: "status_label",
      dataIndex: "payment_status",
      render: (_text, record) => {
        // 30/9/2026 — ป้าย "ชำระเงินเกิน" (ม่วง) เหลือเฉพาะออเดอร์เงินโอน (manual_transfer)
        // ซึ่งเป็นเคสเดียวที่โรงงานต้องคืนเงินส่วนเกินให้ลูกค้าเอง · ประเภทอื่น (COD / ตัดบัตร /
        // wallet) เงินส่วนเกินไม่ใช่งานของโรงงาน และออเดอร์ที่สินค้าถูกยกเลิกจนยอดรวมเหลือ 0
        // แต่ลูกค้าจ่ายมาแล้ว เคยขึ้นป้ายม่วงทั้งที่ไม่มีอะไรผิด · เทียบแบบ lower-case เพราะ
        // payment_type บนสายเป็น "COD" ตัวใหญ่แต่ "manual_transfer" ตัวเล็ก (EPaymentType)
        const statusName = getPaymentStatusName(_text);
        const isManualTransfer = record.payment_type?.toLowerCase() === "manual_transfer";
        let restPrice = -(
          record.total_order_price +
          record.transport_price -
          (record.discount_amount + (record.payment_amount ))
        );
        return (
          <Typography>
            {isManualTransfer && restPrice > 0 ? (
              <Tag color="magenta">ชำระเงินเกิน</Tag>
            ) : (
              <Tag color={statusName?.color}>{statusName?.th}</Tag>
            )}
          </Typography>
        );
      },
    },
    {
      title: "ยอดเงินค้างชำระ",
      key: "rest_price",
      render: (_text, record) => {
        let restPrice = -(
          record.total_order_price +
          record.transport_price -
          (record.discount_amount + (record.payment_amount  ))
          - (record.pay_point)
        );
        const isManualTransfer = record.payment_type?.toLowerCase() === "manual_transfer";
        return (
          <>
            {/* 30/9/2026 — คู่กับป้ายสถานะ: ยอดที่จ่ายเกินโชว์เฉพาะออเดอร์เงินโอน
                (manual_transfer) ที่โรงงานต้องคืนเงินเอง · ประเภทอื่นขึ้น "-" เหมือนเคสจ่ายพอดี
                เพราะช่องนี้คือ "ยอดค้างชำระ" และออเดอร์ที่จ่ายเกินไม่มียอดค้าง */}
            {restPrice > 0 && isManualTransfer && (
              <Typography>
                {numberFormat(restPrice)}
              </Typography>
            )}
            {(restPrice === 0 || (restPrice > 0 && !isManualTransfer)) && (
              <Typography>
                -
              </Typography>
            )}
            {restPrice < 0 && (
              <Typography style={{ color: "red" }}>
                {numberFormat(restPrice)}
              </Typography>
            )}
          </>
        );
      },
    },
    {
      title: "จังหวัดปลายทาง",
      dataIndex: "province_name",
      render: (_text) => {
        return <Typography>{_text}</Typography>;
      },
    },
    {
      title: "สถานะการออก Label",
      key: "shipping_label_status",
      width: SHIPPING_LABEL_STATUS_COLUMN_WIDTH,
      align: "center",
      fixed: "right",
      render: (_text, record) => (
        <ShippingLabelStatusCell
          record={record}
          queueStatus={queueStatus[record.order_id]}
          mode="shipped"
          hasSelfShip={hasSelfByOrderId[record.order_id] ?? false}
          onOpenShippingModal={setRetryOrderId}
        />
      ),
    },
    {
      title: "คืนเงิน",
      key: "refund",
      align: "center",
      width: 100,
      fixed: "right",
      render: (_text, record) => {
        let restPrice = -(
          record.total_order_price +
          record.transport_price -
          (record.discount_amount + (record.payment_amount ))
        );
        return (
          <>
            {restPrice > 0 && (
              <Button
                type="default"
                size="small"
                style={{
                  fontSize: "10px",
                }}
                onClick={() => {
                  dispatch(setLoading(true));

                  mutate(
                    {
                      key_type: "order_id",
                      group_by: "factory",
                      items: [record.order_id],
                      factory_id: factory_id,
                    },
                    {
                      onSuccess: async (data) => {
                        setModalData({
                          ...data.data[0],
                          payment_status: record.payment_status,
                          final_price: restPrice,
                        });

                        await new Promise((resolve) =>
                          setTimeout(resolve, 200)
                        );

                        dispatch(setLoading(false));

                        setShowRefund(true);
                      },
                      onError: (error) => {
                        console.log(error);
                        dispatch(setLoading(false));
                      },
                    }
                  );
                }}
              >
                คืนเงิน
              </Button>
            )}
          </>
        );
      },
    },
    {
      title: "เครื่องมือ",
      align: "center",
      width: 100,
      fixed: "right",
      render: (_text, record) => {
        let restPrice = -(
          record.total_order_price +
          record.transport_price -
          (record.discount_amount + record.payment_amount)
        );
        return (
          <>
            <Button
              size="small"
              type="default"
              
              onClick={() => {
                window.open(`/print-deliverynote/${record?.order_id}`);
              }}
              style={{
                marginRight: "5px",
                  fontSize: "10px"
                }}
            >
              ใบส่งของ
              {/*<PrinterOutlined />*/}
            </Button>
            {/* ซ่อนปุ่ม "ยืนยัน" (ยืนยันลูกค้าได้รับสินค้า) ไว้ก่อนตามที่ขอ — โค้ดยังอยู่ครบ
            เผื่อเอากลับมาใช้ทีหลัง (updateOrderComplete/useUpdateOrderComplete ยังคง import
            ไว้ด้านบนของไฟล์)
            <Popconfirm
              title="ยืนยันลูกค้าได้รับสินค้า"
              description={
                <Typography.Text>
                  คุณต้องการยืนยันว่าลูกค้าได้รับสินค้าแล้ว
                  <br /> หากถูกต้องแล้วจึงกดปุ่ม ยืนยัน
                </Typography.Text>
              }
              okText="ยืนยัน"
              cancelText="กลับ"
              onConfirm={() => {
                updateOrderComplete(
                  {
                    order_id: record.order_id,
                  },
                  {
                    onSuccess: (res) => {
                      if (res.status === "success") {
                        message.success("ยืนยัน ลูกค้า ได้รับสินค้าแล้วสำเร็จ");
                        refetchData();
                        refetchTo && refetchTo();
                      } else {
                        message.error(
                          `ยืนยัน ลูกค้า ได้รับสินค้าแล้วไม่สำเร็จ: ${res.error_message}`
                        );
                      }
                    },
                    onError: (error) => {
                      message.error(
                        `ยืนยัน ลูกค้า ได้รับสินค้าแล้วไม่สำเร็จ: ${error.message}`
                      );
                    },
                  }
                );
              }}
            >
              <Button
                type="default"
                size="small"
                style={{
                  fontSize: "10px",
                }}
              >
                ยืนยัน
              </Button>
            </Popconfirm> */}
          </>
        );
      },
    },
  ];

  let filtersColumns: (
    | ColumnGroupType<GetDataOrderListItems>
    | ColumnType<GetDataOrderListItems>
  )[] = columns;
  if (IsHiddenPersonInfo) {
    filtersColumns = columns.filter(
      (column) => column.key !== "ref_name" && column.key !== "ref_telephone"
    );
  }

    if (!use_platform_transport) {
    filtersColumns = filtersColumns.filter(
      (column) => column.key !== "status_label"
    );
  }

  if (use_platform_transport) {
    filtersColumns = filtersColumns.filter(
    //  (column) => column.key !== "datasource_text" && column.key !== "rest_price" && column.key !== "refund" && column.key !== "payment_type"
      (column) => column.key !== "datasource_text" && column.key !== "rest_price" && column.key !== "refund" 
    );
  }

  // แสดงคอลัมน์สถานะ Label ส่งเองเมื่อมี Speed merchant (สอดคล้องปุ่มออก Label ที่เตรียมจัดส่ง)
  if (!hasSpeedMerchant) {
    filtersColumns = filtersColumns.filter(
      (column) => column.key !== "shipping_label_status"
    );
  }


  return (
    <div>
      <Table
        rowKey="order_id"
        size="small"
        columns={filtersColumns}
        dataSource={data?.order_data}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: parseInt(data?.total_record as string),
          onChange: (page, pageSize) => {
            dispatch(setDataShippedOffset(pageSize! * (page! - 1)));
            dispatch(setDataShippedLimit(pageSize!));
          },
        }}
        scroll={{ x: true }}
      />
      <ModalOderDetail
        open={showDetail}
        onClose={() => {
          setShowDetail(false);
        }}
        item={modalData as GetOrderDetailData}
        childrenBank={childrenBank}
        refetchData={refetchData}
        statusPage="shipped"
        refetchPrepare={refetchTo}
      />
      <ModalRefundPayment
        open={showRefund}
        refund_process={"overpay"}
        onClose={() => {
          setShowRefund(false);
        }}
        item={modalData as GetOrderDetailData}
        refetchData={refetchData}
      />

      <ShippingResultModal
        visible={!!retryOrderId}
        orderId={retryOrderId}
        onClose={() => setRetryOrderId(null)}
        refetchData={() => {
          refetchData();
          refetchTo?.();
          const orderIds = data?.order_data.map((o) => o.order_id) || [];
          if (orderIds.length > 0) {
            apiGetShippingQueueStatus(orderIds)
              .then((res) => {
                if (res.status === "success" && res.data) setQueueStatus(res.data);
              })
              .catch((e) => console.error(e));
          }
        }}
      />
    </div>
  );
};

export default OrderTableList;
